<header class="site-header">
  <!-- ── Mobile bar (hamburger + logo + quotes/favorites) ──────── -->
  <div class="mobile-bar">
    <button
      type="button"
      class="mobile-bar__menu-btn"
      (click)="toggleMobileMenu()"
      [attr.aria-expanded]="mobileMenuOpen()"
      [attr.aria-label]="'header.mobileMenu.open' | translate"
    >
      <span class="mdi mdi-menu" aria-hidden="true"></span>
    </button>

    <a routerLink="/" class="brand" [attr.aria-label]="'header.homeAriaLabel' | translate">
      <img
        ngSrc="/common/logo.png"
        width="690"
        height="153"
        priority
        class="brand__logo brand__logo--mobile"
        alt="Sourcing Souk - Source Smarter"
      />
    </a>

    @if (!isAuthRoute() && isAuthenticated()) {
      <div class="mobile-bar__icons">
        <a routerLink="/quotes" class="mobile-bar__icon-link" [attr.aria-label]="'header.quotes.label' | translate">
          <span class="main-bar__nav-icon-wrap">
            <span class="mdi mdi-message-text-outline" aria-hidden="true"></span>
            @if (quotesCount() > 0) {
              <span class="main-bar__nav-badge">{{ quotesCount() > 99 ? '99+' : quotesCount() }}</span>
            }
          </span>
        </a>
        <a routerLink="/favorites" class="mobile-bar__icon-link" [attr.aria-label]="'header.favorites.label' | translate">
          <span class="main-bar__nav-icon-wrap">
            <span class="mdi mdi-star-outline" aria-hidden="true"></span>
            @if (favoritesCount() > 0) {
              <span class="main-bar__nav-badge">{{ favoritesCount() > 99 ? '99+' : favoritesCount() }}</span>
            }
          </span>
        </a>
      </div>
    } @else if (!isAuthRoute()) {
      <a routerLink="/auth/login" class="btn btn-primary mobile-bar__signin">{{ 'header.signIn' | translate }}</a>
    }
  </div>

  <!-- ── Off-canvas mobile menu ──────────────────────────────── -->
  @if (mobileMenuOpen()) {
    <div class="mobile-drawer-backdrop" (click)="closeMobileMenu()"></div>
    <div class="mobile-drawer" role="dialog" aria-modal="true" [attr.aria-label]="'header.mobileMenu.title' | translate">
      <div class="mobile-drawer__header">
        <img ngSrc="/common/logo.png" width="690" height="153" class="brand__logo" alt="Sourcing Souk" />
        <button
          type="button"
          class="mobile-drawer__close"
          (click)="closeMobileMenu()"
          [attr.aria-label]="'header.mobileMenu.close' | translate"
        >
          <span class="mdi mdi-close" aria-hidden="true"></span>
        </button>
      </div>

      @if (isAuthenticated()) {
        <div class="mobile-drawer__profile">
          <span class="profile-menu__avatar mdi mdi-account-circle" aria-hidden="true"></span>
          <span class="mobile-drawer__profile-name">{{ fullName() }}</span>
        </div>
        <div class="mobile-drawer__section">
          <a routerLink="/settings" class="mobile-drawer__link" (click)="closeMobileMenu()">
            <span class="mdi mdi-cog-outline" aria-hidden="true"></span>
            <span>{{ 'header.profileMenu.settings' | translate }}</span>
          </a>
          <button type="button" class="mobile-drawer__link mobile-drawer__link--danger" (click)="logout()">
            <span class="mdi mdi-logout-variant" aria-hidden="true"></span>
            <span>{{ 'header.profileMenu.logout' | translate }}</span>
          </button>
        </div>
      } @else if (!isAuthRoute()) {
        <div class="mobile-drawer__actions">
          <a routerLink="/register" class="btn btn-outline-primary" (click)="closeMobileMenu()">{{
            'header.register' | translate
          }}</a>
          <a routerLink="/auth/login" class="btn btn-primary" (click)="closeMobileMenu()">{{
            'header.signIn' | translate
          }}</a>
        </div>
      }

      <div class="mobile-drawer__divider"></div>

      <nav class="mobile-drawer__section" [attr.aria-label]="'header.nav.ariaLabel' | translate">
        <button type="button" class="mobile-drawer__link" (click)="goToAllProducts(); closeMobileMenu()">
          <span class="mdi mdi-view-grid" aria-hidden="true"></span>
          <span>{{ 'header.nav.allProducts' | translate }}</span>
        </button>

        <button
          type="button"
          class="mobile-drawer__link"
          [attr.aria-expanded]="mobileCategoriesOpen()"
          (click)="toggleMobileCategories()"
        >
          <span class="mdi mdi-view-grid-outline" aria-hidden="true"></span>
          <span>{{ 'header.nav.categoriesLabel' | translate }}</span>
          <span
            class="mdi mdi-chevron-down mobile-drawer__link-chevron"
            [class.mobile-drawer__link-chevron--open]="mobileCategoriesOpen()"
            aria-hidden="true"
          ></span>
        </button>
        @if (mobileCategoriesOpen()) {
          <div class="mobile-drawer__subsection">
            @if (categoriesLoading()) {
              <div class="category-bar__panel-loading">
                <span class="spinner-border spinner-border-sm"></span>
              </div>
            } @else if (topLevelCategories().length === 0) {
              <p class="category-bar__panel-empty">{{ 'header.nav.categoriesEmpty' | translate }}</p>
            } @else {
              @for (category of topLevelCategories(); track category.id) {
                <button
                  type="button"
                  class="mobile-drawer__sublink"
                  (click)="selectCategory(category); closeMobileMenu()"
                >
                  {{ category.name }}
                </button>
              }
            }
          </div>
        }

        <button
          type="button"
          class="mobile-drawer__link"
          [attr.aria-expanded]="mobileIdeasOpen()"
          (click)="toggleMobileIdeas()"
        >
          <span class="mdi mdi-party-popper" aria-hidden="true"></span>
          <span>{{ 'header.nav.ideasLabel' | translate }}</span>
          <span
            class="mdi mdi-chevron-down mobile-drawer__link-chevron"
            [class.mobile-drawer__link-chevron--open]="mobileIdeasOpen()"
            aria-hidden="true"
          ></span>
        </button>
        @if (mobileIdeasOpen()) {
          <div class="mobile-drawer__subsection">
            @if (occasions().length === 0) {
              <p class="category-bar__panel-empty">{{ 'header.nav.ideasEmpty' | translate }}</p>
            } @else {
              @if (featuredOccasions().length > 0) {
                <span class="category-bar__panel-eyebrow">{{ 'header.nav.featuredLabel' | translate }}</span>
                @for (occasion of featuredOccasions(); track occasion.id) {
                  <button
                    type="button"
                    class="mobile-drawer__sublink mobile-drawer__sublink--featured"
                    (click)="selectOccasion(occasion); closeMobileMenu()"
                  >
                    <span class="mdi mdi-star category-bar__panel-star" aria-hidden="true"></span>
                    {{ occasion.name }}
                  </button>
                }
                <div class="category-bar__panel-divider"></div>
              }
              @for (occasion of nonFeaturedOccasions(); track occasion.id) {
                <button
                  type="button"
                  class="mobile-drawer__sublink"
                  (click)="selectOccasion(occasion); closeMobileMenu()"
                >
                  {{ occasion.name }}
                </button>
              }
            }
          </div>
        }

        <button type="button" class="mobile-drawer__link" (click)="quickFilterExpress(); closeMobileMenu()">
          <span class="mdi mdi-truck-fast-outline" aria-hidden="true"></span>
          <span>{{ 'header.nav.express' | translate }}</span>
        </button>
        <button type="button" class="mobile-drawer__link" (click)="quickFilterUaeStock(); closeMobileMenu()">
          <span class="mdi mdi-warehouse" aria-hidden="true"></span>
          <span>{{ 'header.nav.uaeStock' | translate }}</span>
        </button>
        <button type="button" class="mobile-drawer__link" (click)="quickFilterSustainable(); closeMobileMenu()">
          <span class="mdi mdi-leaf" aria-hidden="true"></span>
          <span>{{ 'header.nav.sustainable' | translate }}</span>
        </button>
        <button
          type="button"
          class="mobile-drawer__link"
          (click)="notImplemented('header.nav.howItWorksComingSoon'); closeMobileMenu()"
        >
          <span class="mdi mdi-help-circle-outline" aria-hidden="true"></span>
          <span>{{ 'header.nav.howItWorks' | translate }}</span>
        </button>
        <button type="button" class="mobile-drawer__link" (click)="quickFilterFastResponse(); closeMobileMenu()">
          <span class="mdi mdi-clock-fast" aria-hidden="true"></span>
          <span>{{ 'header.nav.loadTime' | translate }}</span>
        </button>
      </nav>

      <div class="mobile-drawer__divider"></div>

      <div class="mobile-drawer__section">
        <a class="mobile-drawer__link" href="tel:+3200152876670">
          <span class="mdi mdi-phone-outline" aria-hidden="true"></span>
          <span>+32 (0) 15 28 76 67</span>
        </a>
        <div class="mobile-drawer__lang">
          <span class="mobile-drawer__lang-label">{{ 'header.utilityBar.language' | translate }}</span>
          <div class="mobile-drawer__lang-options">
            @for (option of language.languages; track option.code) {
              <button
                type="button"
                class="mobile-drawer__lang-option"
                [class.mobile-drawer__lang-option--active]="language.currentLanguage() === option.code"
                (click)="selectLanguage(option.code)"
              >
                {{ option.nativeLabel }}
              </button>
            }
          </div>
        </div>
      </div>
    </div>
  }

  <!-- ── Desktop bars ─────────────────────────────────────────── -->
  <div class="site-header__desktop">
  <!-- ── Utility bar ─────────────────────────────────────────── -->
  <div class="utility-bar">
    <div class="utility-bar__inner">
      <div class="utility-bar__left">
        <button
          type="button"
          class="utility-switcher"
          (click)="notImplemented('header.utilityBar.countryComingSoon')"
        >
          <span>{{ 'header.utilityBar.country' | translate }}</span>
          <span class="mdi mdi-chevron-down" aria-hidden="true"></span>
        </button>

        <div class="utility-switcher-dropdown">
          <button
            type="button"
            class="utility-switcher"
            [attr.aria-expanded]="languageMenuOpen()"
            aria-haspopup="true"
            (click)="toggleLanguageMenu($event)"
          >
            <span
              >{{ 'header.utilityBar.language' | translate }}:
              {{ language.getLanguageOption(language.currentLanguage()).nativeLabel }}</span
            >
            <span class="mdi mdi-chevron-down" aria-hidden="true"></span>
          </button>
          @if (languageMenuOpen()) {
            <div class="utility-switcher-dropdown__panel">
              @for (option of language.languages; track option.code) {
                <button
                  type="button"
                  class="utility-switcher-dropdown__option"
                  [class.utility-switcher-dropdown__option--active]="
                    language.currentLanguage() === option.code
                  "
                  (click)="selectLanguage(option.code)"
                >
                  {{ option.nativeLabel }}
                </button>
              }
            </div>
          }
        </div>

        <button
          type="button"
          class="utility-switcher"
          (click)="notImplemented('header.utilityBar.currencyComingSoon')"
        >
          <span>{{ 'header.utilityBar.currency' | translate }}</span>
          <span class="mdi mdi-chevron-down" aria-hidden="true"></span>
        </button>
      </div>
      <div class="utility-bar__right">
        <a
          class="utility-phone"
          href="tel:+3200152876670"
          [attr.aria-label]="'header.phone.ariaLabel' | translate"
        >
          <span class="mdi mdi-phone-outline" aria-hidden="true"></span>
          <span>+32 (0) 15 28 76 67</span>
        </a>
        @if (isAuthenticated()) {
          <div class="utility-switcher-dropdown profile-menu">
            <button
              type="button"
              class="profile-menu__trigger"
              [attr.aria-expanded]="profileMenuOpen()"
              aria-haspopup="true"
              (click)="toggleProfileMenu($event)"
            >
              <span class="profile-menu__avatar mdi mdi-account-circle" aria-hidden="true"></span>
              <span class="profile-menu__name">{{ fullName() }}</span>
              <span class="mdi mdi-chevron-down" aria-hidden="true"></span>
            </button>
            @if (profileMenuOpen()) {
              <div class="profile-menu__panel">
                <a
                  routerLink="/settings"
                  class="profile-menu__item"
                  (click)="profileMenuOpen.set(false)"
                >
                  <span
                    class="profile-menu__item-icon profile-menu__item-icon--settings mdi mdi-cog-outline"
                    aria-hidden="true"
                  ></span>
                  {{ 'header.profileMenu.settings' | translate }}
                </a>
                <button
                  type="button"
                  class="profile-menu__item profile-menu__item--logout"
                  (click)="logout()"
                >
                  <span
                    class="profile-menu__item-icon profile-menu__item-icon--logout mdi mdi-logout-variant"
                    aria-hidden="true"
                  ></span>
                  {{ 'header.profileMenu.logout' | translate }}
                </button>
              </div>
            }
          </div>
        }
      </div>
    </div>
  </div>

  <!-- ── Main bar ────────────────────────────────────────────── -->
  <div class="main-bar">
    <div class="main-bar__inner">
      <a routerLink="/" class="brand" [attr.aria-label]="'header.homeAriaLabel' | translate">
        <img
          ngSrc="/common/logo.png"
          width="690"
          height="153"
          priority
          class="brand__logo"
          alt="Sourcing Souk - Source Smarter"
        />
      </a>
      @if (!isAuthRoute()) {
        @if (isAuthenticated()) {
          <div class="main-bar__nav">
            <a routerLink="/quotes" class="main-bar__nav-link">
              <span class="main-bar__nav-icon-wrap">
                <span class="mdi mdi-message-text-outline" aria-hidden="true"></span>
                @if (quotesCount() > 0) {
                  <span class="main-bar__nav-badge" [attr.aria-label]="quotesCount()">{{
                    quotesCount() > 99 ? '99+' : quotesCount()
                  }}</span>
                }
              </span>
              <span>{{ 'header.quotes.label' | translate }}</span>
            </a>
            <a routerLink="/favorites" class="main-bar__nav-link">
              <span class="main-bar__nav-icon-wrap">
                <span class="mdi mdi-star-outline" aria-hidden="true"></span>
                @if (favoritesCount() > 0) {
                  <span class="main-bar__nav-badge" [attr.aria-label]="favoritesCount()">{{
                    favoritesCount() > 99 ? '99+' : favoritesCount()
                  }}</span>
                }
              </span>
              <span>{{ 'header.favorites.label' | translate }}</span>
            </a>
          </div>
        } @else {
          <div class="main-bar__actions">
            <a routerLink="/register" class="btn btn-outline-primary main-bar__btn">{{
              'header.register' | translate
            }}</a>
            <a routerLink="/auth/login" class="btn btn-primary main-bar__btn">{{
              'header.signIn' | translate
            }}</a>
          </div>
        }
      }
    </div>
  </div>

  <!-- ── Category quick-links bar ────────────────────────────── -->
  <nav class="category-bar" [attr.aria-label]="'header.nav.ariaLabel' | translate">
    <div class="category-bar__inner">
      <button
        type="button"
        class="category-bar__link category-bar__link--all"
        (click)="goToAllProducts()"
      >
        <span class="mdi mdi-view-grid" aria-hidden="true"></span>
        <span>{{ 'header.nav.allProducts' | translate }}</span>
      </button>

      <div class="category-bar__dropdown">
        <button
          type="button"
          class="category-bar__link"
          [attr.aria-expanded]="categoriesOpen()"
          aria-haspopup="true"
          (click)="toggleCategories($event)"
        >
          <span class="mdi mdi-view-grid-outline" aria-hidden="true"></span>
          <span>{{ 'header.nav.categoriesLabel' | translate }}</span>
          <span class="mdi mdi-chevron-down" aria-hidden="true"></span>
        </button>
        @if (categoriesOpen()) {
          <div class="category-bar__panel">
            @if (categoriesLoading()) {
              <div class="category-bar__panel-loading">
                <span class="spinner-border spinner-border-sm"></span>
              </div>
            } @else if (topLevelCategories().length === 0) {
              <p class="category-bar__panel-empty">
                {{ 'header.nav.categoriesEmpty' | translate }}
              </p>
            } @else {
              @for (category of topLevelCategories(); track category.id) {
                <button
                  type="button"
                  class="category-bar__panel-link"
                  (click)="selectCategory(category)"
                >
                  {{ category.name }}
                </button>
              }
            }
          </div>
        }
      </div>

      <div class="category-bar__dropdown">
        <button
          type="button"
          class="category-bar__link"
          [attr.aria-expanded]="ideasOpen()"
          aria-haspopup="true"
          (click)="toggleIdeas($event)"
        >
          <span class="mdi mdi-party-popper" aria-hidden="true"></span>
          <span>{{ 'header.nav.ideasLabel' | translate }}</span>
          <span class="mdi mdi-chevron-down" aria-hidden="true"></span>
        </button>
        @if (ideasOpen()) {
          <div class="category-bar__panel">
            @if (occasions().length === 0) {
              <p class="category-bar__panel-empty">
                {{ 'header.nav.ideasEmpty' | translate }}
              </p>
            } @else {
              @if (featuredOccasions().length > 0) {
                <span class="category-bar__panel-eyebrow">{{ 'header.nav.featuredLabel' | translate }}</span>
                @for (occasion of featuredOccasions(); track occasion.id) {
                  <button
                    type="button"
                    class="category-bar__panel-link category-bar__panel-link--featured"
                    (click)="selectOccasion(occasion)"
                  >
                    <span class="mdi mdi-star category-bar__panel-star" aria-hidden="true"></span>
                    {{ occasion.name }}
                  </button>
                }
                <div class="category-bar__panel-divider"></div>
              }
              @for (occasion of nonFeaturedOccasions(); track occasion.id) {
                <button
                  type="button"
                  class="category-bar__panel-link"
                  (click)="selectOccasion(occasion)"
                >
                  {{ occasion.name }}
                </button>
              }
            }
          </div>
        }
      </div>

      <button type="button" class="category-bar__link" (click)="quickFilterExpress()">
        <span class="mdi mdi-truck-fast-outline" aria-hidden="true"></span>
        <span>{{ 'header.nav.express' | translate }}</span>
      </button>
      <button type="button" class="category-bar__link" (click)="quickFilterUaeStock()">
        <span class="mdi mdi-warehouse" aria-hidden="true"></span>
        <span>{{ 'header.nav.uaeStock' | translate }}</span>
      </button>
      <button type="button" class="category-bar__link" (click)="quickFilterSustainable()">
        <span class="mdi mdi-leaf" aria-hidden="true"></span>
        <span>{{ 'header.nav.sustainable' | translate }}</span>
      </button>
      <div class="category-bar__dropdown">
        <button
          type="button"
          class="category-bar__link"
          [attr.aria-expanded]="moreOpen()"
          aria-haspopup="true"
          (click)="toggleMore($event)"
        >
          <span class="mdi mdi-dots-horizontal-circle-outline" aria-hidden="true"></span>
          <span>{{ 'header.nav.more' | translate }}</span>
          <span class="mdi mdi-chevron-down" aria-hidden="true"></span>
        </button>
        @if (moreOpen()) {
          <div class="category-bar__panel">
            <button
              type="button"
              class="category-bar__panel-link"
              (click)="notImplemented('header.nav.howItWorksComingSoon')"
            >
              <span class="mdi mdi-help-circle-outline" aria-hidden="true"></span>
              {{ 'header.nav.howItWorks' | translate }}
            </button>
            <button type="button" class="category-bar__panel-link" (click)="quickFilterFastResponse()">
              <span class="mdi mdi-clock-fast" aria-hidden="true"></span>
              {{ 'header.nav.loadTime' | translate }}
            </button>
          </div>
        }
      </div>
    </div>
  </nav>
  </div>
</header>
